import React, { useEffect, useState } from 'react';
import {
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Feedback as FeedbackIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import {
  type PatientUpdatePayload,
  type RejectionModalContentProps,
  RejectionReason,
} from '../../types';
import { PatientUpdateForm } from '../edit/PatientUpdateForm';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'edit' | 'feedback' | 'confirmation';

/**
 * Patient update-specific rejection modal content component
 * Provides options specific to patient update suggestion rejections
 */
export const PatientUpdateRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onAccept,
  isLoading = false,
}) => {
  const { action } = context;
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [editedPayload, setEditedPayload] = useState<PatientUpdatePayload>(
    action.action_payload as PatientUpdatePayload,
  );
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const [pendingRejectionReason, setPendingRejectionReason] = useState<RejectionReason | null>(
    null,
  );

  const patientUpdateData = action.action_payload as PatientUpdatePayload;
  const operationCount = patientUpdateData.operations.length;

  // Auto-close countdown for confirmation steps
  useEffect(() => {
    if (currentStep === 'confirmation' && autoCloseCountdown === null) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else if (autoCloseCountdown === 0 && pendingRejectionReason) {
      // When countdown reaches 0, trigger rejection
      if (currentStep === 'confirmation') {
        onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
      }
    }
  }, [autoCloseCountdown, onReject, pendingRejectionReason, currentStep]);

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        setCurrentStep('edit');
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setPendingRejectionReason(reason);
        setCurrentStep('confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleEditConfirm = () => {
    // Accept with the edited payload
    onAccept?.(action.action_id, editedPayload);
  };

  const handleEditReject = () => {
    // Still reject after editing
    onReject(RejectionReason.WRONG_SUGGESTION);
  };

  const handleFeedbackSubmit = () => {
    // Pass rejection with feedback
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
    setPendingRejectionReason(null);
    // Reset edited payload to original
    setEditedPayload(patientUpdateData);
  };

  // Type guard to ensure we have patient update data
  if (action.action_type !== 'patient_update') {
    console.error('PatientUpdateRejection received non-patient-update action');
    return null;
  }

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Patient Update Summary */}
              <Alert severity="info" icon={<PersonIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested patient information updates:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {operationCount} field{operationCount !== 1 ? 's' : ''} from{' '}
                    <strong>{patientUpdateData.form_title}</strong>
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        These updates are incorrect
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        I want to modify the proposed values before accepting
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don&apos;t want patient update suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off patient update suggestions for all future forms
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting these updates
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'edit' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Patient Updates
                </Typography>
              </Box>

              <Alert severity="info">
                <Typography variant="body2">
                  Modify the proposed values below, then accept the updates.
                </Typography>
              </Alert>

              {/* Patient Update Form */}
              <PatientUpdateForm
                payload={editedPayload}
                onChange={setEditedPayload}
                readOnly={false}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleEditReject} variant="outlined" color="error">
                  Still Reject
                </Button>
                <Button onClick={handleEditConfirm} variant="contained" color="primary">
                  Accept Changes
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting these patient
                information updates.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => {
                  setFeedbackText(e.target.value);
                }}
                placeholder="Please describe why these patient updates weren't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.OTHER);
                  }}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Patient Update Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive patient information update suggestions from forms. You
                can re-enable this feature anytime in your preferences.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  // Pass rejection when user clicks "Close"
                  onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
